import ExportDataIcon from "@mui/icons-material/OutputRounded";
import { Alert, Button, Container, Divider, Typography } from "@mui/material";
import { Fragment, useCallback, useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import LoaderBox from "@entrilia/shared/components/LoaderBox";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { OrganizationContactRole } from "../../../../api/types/userManagementTypes";
import useClientContext from "../../../../context/useClientContext";
import GeneralPageFooter from "../../../common/GeneralPageFooter";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import AddOrganizationContactDialog from "./AddOrganizationContactDialog";
import ExportOrganizationContactsActivityLogDialog from "./ExportOrganizationContactsActivityLogDialog";
import OrganizationContactRoleSection from "./OrganizationContactRoleSection";
import { organizationContactRoles } from "./organizationContactRoles";
import type { RoleExternal, RoleMember } from "./organizationContactsState";
import {
  addRoleExternalsAction,
  addRoleMembersAction,
  getInitialOrganizationContactsState,
  getUpdateOrganizationContactsRequest,
  isOrganizationContactsStateDirty,
  organizationContactsStateFromApi,
  removeRoleExternalAction,
  removeRoleMemberAction,
  setOrganizationContactsAction,
  validateOrganizationContactsState,
} from "./organizationContactsState";

const updateOrganizationContacts = withErrorHandling(adminApi.updateOrganizationContacts);

const OrganizationContactsPage = () => {
  usePageTitle("Organization Contacts");

  const { hasPermissions } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [state, setState] = useState(getInitialOrganizationContactsState);
  const [saving, setSaving] = useState(false);
  const [addContactRole, setAddContactRole] = useState<OrganizationContactRole | null>(null);
  const [showExportActivityLogDialog, setShowExportActivityLogDialog] = useState(false);

  const [contacts, fetchContactsError, { setData: setContacts }] = useFetch(adminApi.getOrganizationContacts, {
    onSetData: (data) => setState(setOrganizationContactsAction(data)),
  });

  const persistedState = useMemo(() => organizationContactsStateFromApi(contacts ?? []), [contacts]);

  const canEdit = hasPermissions(["ManageOrganizationUsers"]);

  const fetchEligibleMembers = useCallback(() => adminApi.getOrganizationMembers("Internal"), []);
  const [membersData, fetchMembersError] = useFetch(fetchEligibleMembers, { disabled: !canEdit });

  const fetchError = fetchContactsError || fetchMembersError;

  if (fetchError) {
    logError(fetchError, "[OrganizationContactsPage] getOrganizationContacts");
    return <DataLoadingFailed />;
  }

  const isLoading = contacts === undefined || (canEdit && membersData === undefined);

  const isDirty = isOrganizationContactsStateDirty(state, persistedState);

  const isValid = validateOrganizationContactsState(state);

  const handleSave = async () => {
    setSaving(true);
    const [updated, error] = await updateOrganizationContacts(getUpdateOrganizationContactsRequest(state));
    setSaving(false);

    if (error) {
      logError(error, "[OrganizationContactsPage] updateOrganizationContacts");
      sendNotificationError(error.message);
      return;
    }

    setContacts(updated);
    setState(setOrganizationContactsAction(updated));
    sendNotification("Organization contacts updated successfully");
  };

  const handleCancel = () => {
    setState(setOrganizationContactsAction(contacts ?? []));
  };

  const handleAddMembers = (role: OrganizationContactRole, toAdd: RoleMember[]) => {
    setState(addRoleMembersAction(role, toAdd));
  };

  const handleAddExternals = (role: OrganizationContactRole, toAdd: RoleExternal[]) => {
    setState(addRoleExternalsAction(role, toAdd));
  };

  const handleRemoveMember = (role: OrganizationContactRole, userId: string) => {
    setState(removeRoleMemberAction(role, userId));
  };

  const handleRemoveExternal = (role: OrganizationContactRole, email: string) => {
    setState(removeRoleExternalAction(role, email));
  };

  const activeRoleInfo = organizationContactRoles.find((roleInfo) => roleInfo.role === addContactRole);

  const eligibleMembers = membersData?.users ?? [];

  return (
    <>
      <GeneralPageHeader title="Organization Contacts">
        {canEdit && (
          <>
            <Button variant="text" color="secondary" disabled={!isDirty || saving} onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" disabled={!isDirty || !isValid || saving} loading={saving} onClick={handleSave}>
              Save
            </Button>
            <ActionsMenuButton
              items={[
                {
                  label: "Export Activity Log",
                  icon: <ExportDataIcon />,
                  onClick: () => setShowExportActivityLogDialog(true),
                },
              ]}
            />
          </>
        )}
      </GeneralPageHeader>

      <ScrollableFlexContainer>
        <LoaderBox loading={isLoading}>
          <Container maxWidth="lg" sx={{ py: 2.5, flex: 1 }}>
            <Typography>
              Designate who at your firm should receive specific types of communication from Entrilia.
            </Typography>

            {organizationContactRoles.map((roleInfo, index) => (
              <Fragment key={roleInfo.role}>
                {index > 0 && <Divider />}
                <OrganizationContactRoleSection
                  roleInfo={roleInfo}
                  group={state[roleInfo.role]}
                  canEdit={canEdit}
                  onAddContact={setAddContactRole}
                  onRemoveMember={handleRemoveMember}
                  onRemoveExternal={handleRemoveExternal}
                />
              </Fragment>
            ))}
          </Container>
        </LoaderBox>
      </ScrollableFlexContainer>

      <GeneralPageFooter containerProps={{ sx: { px: 2, justifyContent: "center" } }}>
        <Alert severity="info" sx={{ maxWidth: "lg" }}>
          Organization Owners are responsible for maintaining the accuracy of this page. Entrilia accepts no liability
          for notifications sent to contact information that has not been kept current.
        </Alert>
      </GeneralPageFooter>

      {activeRoleInfo && (
        <AddOrganizationContactDialog
          roleInfo={activeRoleInfo}
          members={eligibleMembers}
          assigned={state[activeRoleInfo.role]}
          onClose={() => setAddContactRole(null)}
          onAddMembers={handleAddMembers}
          onAddExternals={handleAddExternals}
        />
      )}

      {showExportActivityLogDialog && (
        <ExportOrganizationContactsActivityLogDialog onClose={() => setShowExportActivityLogDialog(false)} />
      )}
    </>
  );
};

export default OrganizationContactsPage;
